import { useState, useEffect } from "react";
import { Clock, Coffee, FileText, Globe, Pill, PlaneTakeoff, PenLine, Check, X, Smartphone, Music, Users } from "lucide-react";
import { KO, ES, POLICY, getPolicy, savePolicy } from "./helpers";
const ES_DEFAULT_RULES = ES.policy.rules, ES_DEFAULT_PHONE = ES.policy.phoneRules, ES_DEFAULT_MUSIC = ES.policy.musicRules, ES_DEFAULT_ADDRESS = ES.policy.addressRules;

export function PolicyPage({t,lang,isAdmin}){
  const P=t.policy;
  const KO=lang==="kr";
  const [policy,setPolicy]=useState(null);
  const [saving,setSaving]=useState(false);
  const [editWork,setEditWork]=useState(false);
  const [editSick,setEditSick]=useState(false);
  const [editRules,setEditRules]=useState(false);
  const [editHoliday,setEditHoliday]=useState(false);
  const [editVac,setEditVac]=useState(false);
  const [workForm,setWorkForm]=useState(null);
  const [sickForm,setSickForm]=useState(null);
  const [rulesForm,setRulesForm]=useState(null);
  const [holidayForm,setHolidayForm]=useState(null);
  const [vacForm,setVacForm]=useState(null);

  const [editConduct,setEditConduct]=useState(false);
  const [conductForm,setConductForm]=useState(null);

  useEffect(()=>{ getPolicy().then(setPolicy); },[]);

  const wh = policy ? {start:policy.work_start_en,end:policy.work_end_en,days:policy.work_days_en} : POLICY.workHours;
  const whKo = policy ? {start:policy.work_start_kr,end:policy.work_end_kr,days:policy.work_days_kr} : POLICY.workHoursKo;
  const breaks = policy?.breaks || POLICY.breaks;
  const sickDays = policy?.sick_days ?? POLICY.sickDays;
  const sickHours = policy?.sick_hours ?? POLICY.sickHours;
  const rulesKr = policy?.rules_kr || null;
  const rulesEn = policy?.rules_en || null;
  const es = policy?.es || {};
  const whShow = lang==="kr" ? whKo : lang==="es" ? {start:es.work?.start||wh.start,end:es.work?.end||wh.end,days:es.work?.days||wh.days} : wh;
  const rules = (lang==="kr" ? rulesKr : lang==="es" ? (es.rules||null) : rulesEn) || P.rules;
  const holidayNote = (lang==="kr" ? policy?.holiday_note_kr : lang==="es" ? es.holiday_note : policy?.holiday_note_en) || P.holidayNote;
  const vacTiers = policy?.vacation_tiers || POLICY.vacation.map(v=>v.days);

  const conductRules = policy?.conduct_rules || null;
  const phoneRulesKr = conductRules?.phone_kr || null;
  const phoneRulesEn = conductRules?.phone_en || null;
  const musicRulesKr = conductRules?.music_kr || null;
  const musicRulesEn = conductRules?.music_en || null;
  const addressRulesKr = conductRules?.address_kr || null;
  const addressRulesEn = conductRules?.address_en || null;
  const phoneRules = (KO ? phoneRulesKr : lang==="es" ? conductRules?.phone_es : phoneRulesEn) || P.phoneRules;
  const musicRules = (KO ? musicRulesKr : lang==="es" ? conductRules?.music_es : musicRulesEn) || P.musicRules;
  const addressRules = (KO ? addressRulesKr : lang==="es" ? conductRules?.address_es : addressRulesEn) || P.addressRules;

  function startEditWork(){
    setWorkForm({
      work_start_en: wh.start, work_end_en: wh.end, work_days_en: wh.days,
      work_start_kr: whKo.start, work_end_kr: whKo.end, work_days_kr: whKo.days,
      work_start_es: es.work?.start ?? wh.start, work_end_es: es.work?.end ?? wh.end, work_days_es: es.work?.days ?? wh.days,
      breaks: breaks.map(b=>({...b})),
    });
    setEditWork(true);
  }
  async function saveWork(){
    setSaving(true);
    const {work_start_es,work_end_es,work_days_es,...workRest}=workForm;
    const ok = await savePolicy({...workRest, es:{...es, work:{start:work_start_es,end:work_end_es,days:work_days_es}}});
    if(ok){ const fresh=await getPolicy(); setPolicy(fresh); setEditWork(false); }
    setSaving(false);
  }

  function startEditSick(){
    setSickForm({ sick_days: sickDays, sick_hours: sickHours });
    setEditSick(true);
  }
  async function saveSick(){
    setSaving(true);
    const ok = await savePolicy({ sick_days: Number(sickForm.sick_days), sick_hours: Number(sickForm.sick_hours) });
    if(ok){ const fresh=await getPolicy(); setPolicy(fresh); setEditSick(false); }
    setSaving(false);
  }

  function startEditRules(){
    setRulesForm({
      rules_kr: (rulesKr || P.rules || []).join("\n"),
      rules_en: (rulesEn || P.rules || []).join("\n"),
      rules_es: (es.rules || ES_DEFAULT_RULES || []).join("\n"),
    });
    setEditRules(true);
  }
  async function saveRules(){
    setSaving(true);
    const ok = await savePolicy({
      rules_kr: rulesForm.rules_kr.split("\n").map(s=>s.trim()).filter(Boolean),
      rules_en: rulesForm.rules_en.split("\n").map(s=>s.trim()).filter(Boolean),
      es: {...es, rules: (rulesForm.rules_es||"").split("\n").map(s=>s.trim()).filter(Boolean)},
    });
    if(ok){ const fresh=await getPolicy(); setPolicy(fresh); setEditRules(false); }
    setSaving(false);
  }

  function startEditHoliday(){
    setHolidayForm({
      holiday_note_kr: policy?.holiday_note_kr || P.holidayNote,
      holiday_note_en: policy?.holiday_note_en || P.holidayNote,
      holiday_note_es: es.holiday_note || "",
    });
    setEditHoliday(true);
  }
  async function saveHoliday(){
    setSaving(true);
    const ok = await savePolicy({
      holiday_note_kr: holidayForm.holiday_note_kr,
      holiday_note_en: holidayForm.holiday_note_en,
      es: {...es, holiday_note: holidayForm.holiday_note_es||""},
    });
    if(ok){ const fresh=await getPolicy(); setPolicy(fresh); setEditHoliday(false); }
    setSaving(false);
  }

  function startEditVac(){
    setVacForm([...vacTiers]);
    setEditVac(true);
  }
  async function saveVac(){
    setSaving(true);
    const ok = await savePolicy({ vacation_tiers: vacForm.map(n=>Number(n)||0) });
    if(ok){ const fresh=await getPolicy(); setPolicy(fresh); setEditVac(false); }
    setSaving(false);
  }

  function downloadPolicyPdf(){
    const isKr = KO;
    const vacRows = POLICY.vacation.map((v,i)=>`
      <tr><td>${v.range}</td><td>${vacTiers[i]} ${isKr?"일":"days"}</td></tr>
    `).join("");
    const breaksRows = breaks.map(b=>`
      <tr><td>${isKr?(b.labelKo||b.label):lang==="es"?(b.labelEs||b.label):b.label}</td><td>${b.time}</td><td>${isKr?(b.durKo||b.dur):lang==="es"?(b.durEs||b.dur):b.dur}</td></tr>
    `).join("");
    const rulesList = rules.map(r=>`<li>${r}</li>`).join("");
    const phoneList = phoneRules.map(r=>`<li>${r}</li>`).join("");
    const musicList = musicRules.map(r=>`<li>${r}</li>`).join("");
    const addressList = addressRules.map(r=>`<li>${r}</li>`).join("");

    const html=`<!DOCTYPE html><html><head><meta charset="UTF-8">
    <title>SPRINGBLINDS – ${isKr?"회사 정책":"Company Policy"}</title>
    <style>
      @page{margin:0.75in;}
      body{font-family:Arial,sans-serif;font-size:9.5pt;color:#111;line-height:1.6;}
      .header{display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #1e3a5f;padding-bottom:8px;margin-bottom:16px;}
      .logo{font-weight:900;font-size:13pt;letter-spacing:1px;color:#1e3a5f;}
      .docno{font-size:7.5pt;color:#777;}
      h1{text-align:center;font-size:15pt;font-weight:700;margin:0 0 20px;color:#1e3a5f;}
      .section{font-weight:700;font-size:10.5pt;border-bottom:1.5px solid #1e3a5f;margin:18px 0 8px;padding-bottom:3px;text-transform:uppercase;letter-spacing:.5px;color:#1e3a5f;}
      .sub-section{font-weight:700;font-size:9.5pt;margin:12px 0 4px;color:#333;}
      table{width:100%;border-collapse:collapse;font-size:9pt;margin-bottom:8px;}
      th{background:#1e3a5f;color:#fff;padding:6px 10px;text-align:left;font-weight:700;}
      td{border:1px solid #ccc;padding:5px 10px;}
      tr:nth-child(even) td{background:#f5f7fa;}
      ul{margin:4px 0 8px 18px;padding:0;}
      li{margin:2px 0;}
      .notice{border:1px solid #ccc;padding:10px 14px;font-size:8.5pt;color:#555;background:#fafafa;margin-bottom:14px;}
      .two-col{display:grid;grid-template-columns:1fr 1fr;gap:20px;}
      .field{display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #eee;font-size:9pt;}
      .val{font-weight:700;color:#1e3a5f;}
    </style></head><body>
    <div class="header">
      <div class="logo">SPRINGBLINDS</div>
      <div class="docno">Document No. SR-EBIP-001 | Version: 1.1 | Effective Date: 4/14/2026</div>
    </div>
    <h1>${isKr?"회사 정책":"Work Hours, Breaks, and Leave Policy"}</h1>

    <div class="section">${isKr?"근무 시간 & 휴식":"Working Hours & Breaks"}</div>
    <div class="two-col">
      <div>
        <div class="field"><span>${isKr?"근무일":"Work Days"}</span><span class="val">${whShow.days}</span></div>
        <div class="field"><span>${isKr?"근무시간":"Hours"}</span><span class="val">${whShow.start} – ${whShow.end}</span></div>
      </div>
      <div>
        <table>
          <thead><tr><th>${isKr?"휴식":"Break"}</th><th>${isKr?"시간":"Time"}</th><th>${isKr?"기간":"Duration"}</th></tr></thead>
          <tbody>${breaksRows}</tbody>
        </table>
      </div>
    </div>

    <div class="section">${isKr?"유급 휴가 (근속연수별)":"Paid Vacation (by Service Length)"}</div>
    <table>
      <thead><tr><th>${isKr?"근속연수":"Service Length"}</th><th>${isKr?"연차":"Days"}</th></tr></thead>
      <tbody>${vacRows}</tbody>
    </table>
    <div class="sub-section">${isKr?"휴가 규정":"Vacation Rules"}</div>
    <ul>${rulesList}</ul>

    <div class="section">${isKr?"유급 병가":"Paid Sick Leave"}</div>
    <div class="field"><span>${isKr?"연간 한도":"Annual Limit"}</span><span class="val">${sickDays} ${isKr?"일":"days"} (${sickHours} ${isKr?"시간":"hrs"})</span></div>
    <div class="field"><span>${isKr?"이월":"Carry Over"}</span><span class="val">${isKr?"가능 (최대 40시간)":"Yes (up to 40 hrs)"}</span></div>
    <div class="field"><span>${isKr?"미사용 지급":"Unused Payout"}</span><span class="val">${isKr?"없음":"No payout"}</span></div>
    <div class="field"><span>${isKr?"서류 필요":"Documentation"}</span><span class="val">${isKr?"3일 이상 연속":"3+ consecutive days"}</span></div>

    <div class="section">${isKr?"공휴일 정책":"Holiday Policy"}</div>
    <div class="notice" style="white-space:pre-line">${holidayNote}</div>

    <div class="section">${isKr?"근무 수칙":"Workplace Conduct Policy"}</div>
    <div class="sub-section">${isKr?"휴대폰 정책":"Cell Phone Policy"}</div>
    <ul>${phoneList}</ul>
    <div class="sub-section">${isKr?"음악/오디오 정책":"Music & Audio Policy"}</div>
    <ul>${musicList}</ul>
    <div class="sub-section">${isKr?"호칭 규정":"Respectful Forms of Address"}</div>
    <ul>${addressList}</ul>

    <div style="margin-top:30px;border-top:1px solid #ccc;padding-top:10px;font-size:7.5pt;color:#777;text-align:center;">
      This document is legally binding under federal and New Jersey employment law. By signing, the Employee acknowledges and agrees to all terms and accepts related obligations. It may be used as legal evidence.
    </div>
    <script>window.onload=()=>window.print();</script>
    </body></html>`;

    const w=window.open("","_blank"); w.document.write(html); w.document.close();
  }

  function updateBreak(i,field,val){
    setWorkForm(f=>{
      const nb=[...f.breaks]; nb[i]={...nb[i],[field]:val};
      return {...f,breaks:nb};
    });
  }

  function startEditConduct(){
    setConductForm({
      phone_kr: (phoneRulesKr || P.phoneRules).join("\n"),
      phone_en: (phoneRulesEn || P.phoneRules).join("\n"),
      phone_es: (conductRules?.phone_es || ES_DEFAULT_PHONE).join("\n"),
      music_kr: (musicRulesKr || P.musicRules).join("\n"),
      music_en: (musicRulesEn || P.musicRules).join("\n"),
      music_es: (conductRules?.music_es || ES_DEFAULT_MUSIC).join("\n"),
      address_kr: (addressRulesKr || P.addressRules).join("\n"),
      address_en: (addressRulesEn || P.addressRules).join("\n"),
      address_es: (conductRules?.address_es || ES_DEFAULT_ADDRESS).join("\n"),
    });
    setEditConduct(true);
  }
  async function saveConduct(){
    setSaving(true);
    const ok = await savePolicy({
      conduct_rules: {
        phone_kr: conductForm.phone_kr.split("\n").map(s=>s.trim()).filter(Boolean),
        phone_en: conductForm.phone_en.split("\n").map(s=>s.trim()).filter(Boolean),
        phone_es: (conductForm.phone_es||"").split("\n").map(s=>s.trim()).filter(Boolean),
        music_kr: conductForm.music_kr.split("\n").map(s=>s.trim()).filter(Boolean),
        music_en: conductForm.music_en.split("\n").map(s=>s.trim()).filter(Boolean),
        music_es: (conductForm.music_es||"").split("\n").map(s=>s.trim()).filter(Boolean),
        address_kr: conductForm.address_kr.split("\n").map(s=>s.trim()).filter(Boolean),
        address_en: conductForm.address_en.split("\n").map(s=>s.trim()).filter(Boolean),
        address_es: (conductForm.address_es||"").split("\n").map(s=>s.trim()).filter(Boolean),
      }
    });
    if(ok){ const fresh=await getPolicy(); setPolicy(fresh); setEditConduct(false); }
    setSaving(false);
  }

  return(
    <>
      <div className="ph">
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <div><div className="pt"><FileText size={20}/> {P.title}</div><div className="ps">{P.sub}</div></div>
          {isAdmin && <button onClick={downloadPolicyPdf} className="btn bs" style={{background:"var(--ac)",color:"#fff",whiteSpace:"nowrap",display:"flex",alignItems:"center",gap:6}}>
            <FileText size={14}/> {KO?"PDF 다운로드":lang==="es"?"Descargar PDF":"Download PDF"}
          </button>}
        </div>
      </div>
      <div className="pg">
        <div style={{display:"flex",flexDirection:"column",gap:10,height:"100%"}}>
          <div className="card">
            <div className="ct" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span><Clock size={15}/> {P.workTitle}</span>
              {isAdmin && !editWork && <button className="btn bs bsec" onClick={startEditWork}><PenLine size={14}/></button>}
              {isAdmin && editWork && (
                <span style={{display:"flex",gap:6}}>
                  <button className="btn bs bp" disabled={saving} onClick={saveWork}><Check size={14}/></button>
                  <button className="btn bs bsec" onClick={()=>setEditWork(false)}><X size={14}/></button>
                </span>
              )}
            </div>

            {!editWork && (
              <>
                <div className="pr"><span>{P.workDays}</span><span className="pv">{whShow.days}</span></div>
                <div className="pr" style={{borderBottomColor:"transparent"}}><span>{P.hours}</span><span className="pv">{whShow.start} – {whShow.end}</span></div>
                <hr style={{border:"none",borderTop:"1px solid transparent",margin:"12px 0"}}/>
                <div className="ct" style={{marginBottom:10}}><Coffee size={15}/> {P.breakTitle}</div>
                {breaks.map((b,i)=>(
                  <div key={i} className="pr">
                    <span>{lang==="kr"?(b.labelKo||b.label):lang==="es"?(b.labelEs||b.label):b.label}</span>
                    <span><span className="pv" style={{fontSize:12}}>{b.time}</span> <span style={{color:"var(--tx3)",fontSize:11}}>({lang==="kr"?(b.durKo||b.dur):lang==="es"?(b.durEs||b.dur):b.dur})</span></span>
                  </div>
                ))}
              </>
            )}

            {editWork && workForm && (
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                <div style={{display:"flex",gap:6}}>
                  <input placeholder="Days (EN)" value={workForm.work_days_en} onChange={e=>setWorkForm({...workForm,work_days_en:e.target.value})}/>
                  <input placeholder="요일 (KR)" value={workForm.work_days_kr} onChange={e=>setWorkForm({...workForm,work_days_kr:e.target.value})}/>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <input placeholder="Start (EN)" value={workForm.work_start_en} onChange={e=>setWorkForm({...workForm,work_start_en:e.target.value})}/>
                  <input placeholder="End (EN)" value={workForm.work_end_en} onChange={e=>setWorkForm({...workForm,work_end_en:e.target.value})}/>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <input placeholder="시작 (KR)" value={workForm.work_start_kr} onChange={e=>setWorkForm({...workForm,work_start_kr:e.target.value})}/>
                  <input placeholder="종료 (KR)" value={workForm.work_end_kr} onChange={e=>setWorkForm({...workForm,work_end_kr:e.target.value})}/>
                </div>
                <div style={{display:"flex",gap:6}}>
                  <input placeholder="Días (ES)" value={workForm.work_days_es||""} onChange={e=>setWorkForm({...workForm,work_days_es:e.target.value})}/>
                  <input placeholder="Inicio (ES)" value={workForm.work_start_es||""} onChange={e=>setWorkForm({...workForm,work_start_es:e.target.value})}/>
                  <input placeholder="Fin (ES)" value={workForm.work_end_es||""} onChange={e=>setWorkForm({...workForm,work_end_es:e.target.value})}/>
                </div>
                <div className="ct" style={{marginTop:6}}><Coffee size={14}/> {P.breakTitle}</div>
                {workForm.breaks.map((b,i)=>(
                  <div key={i} style={{display:"flex",flexDirection:"column",gap:4,border:"1px solid var(--bd)",borderRadius:8,padding:8}}>
                    <div style={{display:"flex",gap:6}}>
                      <input placeholder="Label (EN)" value={b.label} onChange={e=>updateBreak(i,"label",e.target.value)}/>
                      <input placeholder="라벨 (KR)" value={b.labelKo} onChange={e=>updateBreak(i,"labelKo",e.target.value)}/>
                      <input placeholder="Etiqueta (ES)" value={b.labelEs||""} onChange={e=>updateBreak(i,"labelEs",e.target.value)}/>
                    </div>
                    <input placeholder="Time" value={b.time} onChange={e=>updateBreak(i,"time",e.target.value)}/>
                    <div style={{display:"flex",gap:6}}>
                      <input placeholder="Duration (EN)" value={b.dur} onChange={e=>updateBreak(i,"dur",e.target.value)}/>
                      <input placeholder="기간 (KR)" value={b.durKo} onChange={e=>updateBreak(i,"durKo",e.target.value)}/>
                      <input placeholder="Duración (ES)" value={b.durEs||""} onChange={e=>updateBreak(i,"durEs",e.target.value)}/>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="card" style={{flex:1}}>
            <div className="ct" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span><PlaneTakeoff size={15}/> {P.vacTitle}</span>
              {isAdmin && !editVac && <button className="btn bs bsec" onClick={startEditVac}><PenLine size={14}/></button>}
              {isAdmin && editVac && (
                <span style={{display:"flex",gap:6}}>
                  <button className="btn bs bp" disabled={saving} onClick={saveVac}><Check size={14}/></button>
                  <button className="btn bs bsec" onClick={()=>setEditVac(false)}><X size={14}/></button>
                </span>
              )}
            </div>
            {!editVac && POLICY.vacation.map((v,i)=>(
              <div key={i} className="pr"><span>{v.range}</span><span className="pv">{vacTiers[i]} {P.dayUnit}</span></div>
            ))}
            {editVac && vacForm && POLICY.vacation.map((v,i)=>(
              <div key={i} className="pr">
                <span>{v.range}</span>
                <input type="number" style={{width:60}} value={vacForm[i]}
                  onChange={e=>{const nf=[...vacForm]; nf[i]=e.target.value; setVacForm(nf);}}/>
              </div>
            ))}
          </div>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:10}}>
          <div className="card">
            <div className="ct" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span><Pill size={15}/> {P.sickTitle}</span>
              {isAdmin && !editSick && <button className="btn bs bsec" onClick={startEditSick}><PenLine size={14}/></button>}
              {isAdmin && editSick && (
                <span style={{display:"flex",gap:6}}>
                  <button className="btn bs bp" disabled={saving} onClick={saveSick}><Check size={14}/></button>
                  <button className="btn bs bsec" onClick={()=>setEditSick(false)}><X size={14}/></button>
                </span>
              )}
            </div>
            {!editSick && (
              <>
                <div className="pr"><span>{P.annualLimit}</span><span className="pv">{sickDays} {P.dayUnit} ({sickHours} {P.hrUnit})</span></div>
                <div className="pr"><span>{P.carryOver}</span><span className="pv" style={{color:"var(--gr)"}}>{P.carryVal}</span></div>
                <div className="pr"><span>{P.noPayOut}</span><span className="pv" style={{color:"var(--re)"}}>{P.noPayVal}</span></div>
                <div className="pr"><span>{P.docReq}</span><span className="pv">{P.docVal}</span></div>
              </>
            )}
            {editSick && sickForm && (
              <div style={{display:"flex",gap:6}}>
                <input type="number" placeholder="Days" value={sickForm.sick_days} onChange={e=>setSickForm({...sickForm,sick_days:e.target.value})}/>
                <input type="number" placeholder="Hours" value={sickForm.sick_hours} onChange={e=>setSickForm({...sickForm,sick_hours:e.target.value})}/>
              </div>
            )}
          </div>
          <div className="card">
            <div className="ct" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span><FileText size={15}/> {P.rulesTitle}</span>
              {isAdmin && !editRules && <button className="btn bs bsec" onClick={startEditRules}><PenLine size={14}/></button>}
              {isAdmin && editRules && (
                <span style={{display:"flex",gap:6}}>
                  <button className="btn bs bp" disabled={saving} onClick={saveRules}><Check size={14}/></button>
                  <button className="btn bs bsec" onClick={()=>setEditRules(false)}><X size={14}/></button>
                </span>
              )}
            </div>
            {!editRules && rules.map((r,i)=><div key={i} className="pru">{r}</div>)}
            {editRules && rulesForm && (
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                <label style={{fontSize:11,color:"var(--tx3)"}}>한글 (한 줄에 하나씩)</label>
                <textarea rows={4} value={rulesForm.rules_kr} onChange={e=>setRulesForm({...rulesForm,rules_kr:e.target.value})}/>
                <label style={{fontSize:11,color:"var(--tx3)"}}>English (one per line)</label>
                <textarea rows={4} value={rulesForm.rules_en} onChange={e=>setRulesForm({...rulesForm,rules_en:e.target.value})}/>
                <label style={{fontSize:11,color:"var(--tx3)"}}>Español (una por línea)</label>
                <textarea rows={4} value={rulesForm.rules_es||""} onChange={e=>setRulesForm({...rulesForm,rules_es:e.target.value})}/>
              </div>
            )}
          </div>
          <div className="card">
            <div className="ct" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <span><Globe size={15}/> {P.holidayTitle}</span>
              {isAdmin && !editHoliday && <button className="btn bs bsec" onClick={startEditHoliday}><PenLine size={14}/></button>}
              {isAdmin && editHoliday && (
                <span style={{display:"flex",gap:6}}>
                  <button className="btn bs bp" disabled={saving} onClick={saveHoliday}><Check size={14}/></button>
                  <button className="btn bs bsec" onClick={()=>setEditHoliday(false)}><X size={14}/></button>
                </span>
              )}
            </div>
            {!editHoliday && <div className="ib" style={{whiteSpace:"pre-line"}}>{holidayNote}</div>}
            {editHoliday && holidayForm && (
              <div style={{display:"flex",flexDirection:"column",gap:6}}>
                <label style={{fontSize:11,color:"var(--tx3)"}}>한글</label>
                <textarea rows={4} value={holidayForm.holiday_note_kr} onChange={e=>setHolidayForm({...holidayForm,holiday_note_kr:e.target.value})}/>
                <label style={{fontSize:11,color:"var(--tx3)"}}>English</label>
                <textarea rows={4} value={holidayForm.holiday_note_en} onChange={e=>setHolidayForm({...holidayForm,holiday_note_en:e.target.value})}/>
                <label style={{fontSize:11,color:"var(--tx3)"}}>Español</label>
                <textarea rows={4} value={holidayForm.holiday_note_es||""} onChange={e=>setHolidayForm({...holidayForm,holiday_note_es:e.target.value})}/>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 근무 수칙 */}
      <div className="card" style={{marginTop:10}}>
        <div className="ct" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span><FileText size={15}/> {P.conductTitle}</span>
          {isAdmin && !editConduct && <button className="btn bs bsec" onClick={startEditConduct}><PenLine size={14}/></button>}
          {isAdmin && editConduct && (
            <span style={{display:"flex",gap:6}}>
              <button className="btn bs bp" disabled={saving} onClick={saveConduct}><Check size={14}/></button>
              <button className="btn bs bsec" onClick={()=>setEditConduct(false)}><X size={14}/></button>
            </span>
          )}
        </div>

        {!editConduct && (
          <>
            <div style={{fontSize:12,color:"var(--tx3)",marginBottom:16}}>{P.conductSub}</div>
            <div style={{marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",gap:6,fontWeight:700,fontSize:13,marginBottom:10}}>
                <Smartphone size={14} color="var(--ac)"/>{P.phoneTitle}
              </div>
              {phoneRules.map((r,i)=><div key={i} className="pru">{r}</div>)}
            </div>
            <hr style={{border:"none",borderTop:"1px solid var(--bd)",margin:"0 0 20px"}}/>
            <div style={{marginBottom:20}}>
              <div style={{display:"flex",alignItems:"center",gap:6,fontWeight:700,fontSize:13,marginBottom:10}}>
                <Music size={14} color="var(--ac)"/>{P.musicTitle}
              </div>
              {musicRules.map((r,i)=><div key={i} className="pru">{r}</div>)}
            </div>
            <hr style={{border:"none",borderTop:"1px solid var(--bd)",margin:"0 0 20px"}}/>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:6,fontWeight:700,fontSize:13,marginBottom:10}}>
                <Users size={14} color="var(--ac)"/>{P.addressTitle}
              </div>
              {addressRules.map((r,i)=><div key={i} className="pru">{r}</div>)}
            </div>
          </>
        )}

        {editConduct && conductForm && (
          <div style={{display:"flex",flexDirection:"column",gap:12}}>
            <div style={{fontWeight:700,fontSize:13,color:"var(--ac)"}}><Smartphone size={13}/> {P.phoneTitle}</div>
            <label style={{fontSize:11,color:"var(--tx3)"}}>한글 (한 줄에 하나씩)</label>
            <textarea rows={5} value={conductForm.phone_kr} onChange={e=>setConductForm({...conductForm,phone_kr:e.target.value})} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:12,resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}/>
            <label style={{fontSize:11,color:"var(--tx3)"}}>English (one per line)</label>
            <textarea rows={5} value={conductForm.phone_en} onChange={e=>setConductForm({...conductForm,phone_en:e.target.value})} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:12,resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}/>
            <label style={{fontSize:11,color:"var(--tx3)"}}>Español (una por línea)</label>
            <textarea rows={5} value={conductForm.phone_es||""} onChange={e=>setConductForm({...conductForm,phone_es:e.target.value})} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontFamily:"inherit",fontSize:13}}/>

            <hr style={{border:"none",borderTop:"1px solid var(--bd)"}}/>
            <div style={{fontWeight:700,fontSize:13,color:"var(--ac)"}}><Music size={13}/> {P.musicTitle}</div>
            <label style={{fontSize:11,color:"var(--tx3)"}}>한글 (한 줄에 하나씩)</label>
            <textarea rows={5} value={conductForm.music_kr} onChange={e=>setConductForm({...conductForm,music_kr:e.target.value})} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:12,resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}/>
            <label style={{fontSize:11,color:"var(--tx3)"}}>English (one per line)</label>
            <textarea rows={5} value={conductForm.music_en} onChange={e=>setConductForm({...conductForm,music_en:e.target.value})} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:12,resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}/>
            <label style={{fontSize:11,color:"var(--tx3)"}}>Español (una por línea)</label>
            <textarea rows={5} value={conductForm.music_es||""} onChange={e=>setConductForm({...conductForm,music_es:e.target.value})} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontFamily:"inherit",fontSize:13}}/>

            <hr style={{border:"none",borderTop:"1px solid var(--bd)"}}/>
            <div style={{fontWeight:700,fontSize:13,color:"var(--ac)"}}><Users size={13}/> {P.addressTitle}</div>
            <label style={{fontSize:11,color:"var(--tx3)"}}>한글 (한 줄에 하나씩)</label>
            <textarea rows={3} value={conductForm.address_kr} onChange={e=>setConductForm({...conductForm,address_kr:e.target.value})} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:12,resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}/>
            <label style={{fontSize:11,color:"var(--tx3)"}}>English (one per line)</label>
            <textarea rows={3} value={conductForm.address_en} onChange={e=>setConductForm({...conductForm,address_en:e.target.value})} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontSize:12,resize:"vertical",fontFamily:"inherit",boxSizing:"border-box"}}/>
            <label style={{fontSize:11,color:"var(--tx3)"}}>Español (una por línea)</label>
            <textarea rows={3} value={conductForm.address_es||""} onChange={e=>setConductForm({...conductForm,address_es:e.target.value})} style={{width:"100%",padding:"8px 10px",borderRadius:8,border:"1px solid var(--bd)",background:"var(--sf2)",color:"var(--tx)",fontFamily:"inherit",fontSize:13}}/>
          </div>
        )}
      </div>
    </>
  );
}
